import { FC, Fragment, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { getResultAnswers } from 'store/player/playerAsync';
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectPreviewPageMode, selectResult } from 'store/player/playerSelectors';
import IQuestion from 'models/Question';
// Components
import AppBar from 'components/AppBar';
import SocialShare from 'components/SocialShare';
import PublicDevicePlayable from 'components/PublicDevicePlayable';
import PoweredBy from 'components/PoweredBy';
import PlayerSpot from '../../components/PlayerSpot';
import { Button } from 'components/Buttons';
// Icons
import { FiRefreshCcw } from 'react-icons/fi';
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { teamSelectorFormations } from 'utilities/Utilities';

const TeamBoard:FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug:string }>();

  const dispatch = useAppDispatch();

  const quiz = useAppSelector(selectQuiz);
  const result = useAppSelector(selectResult);
  const previewPageMode = useAppSelector(selectPreviewPageMode);

  const { socialShare } = quiz.resultsPage;

  const handleGetResult = () => {
    dispatch(getResultAnswers({ quizName: slug }));
  }

  useEffect(() => {
    if (!previewPageMode) return;
    handleGetResult();
    // eslint-disable-next-line
  }, [previewPageMode]);

  if (!result) return null;

  const answers = quiz.questions.map((question: IQuestion) => {
    const resultItem = result.results.find((r: any) => r.questionRef === question.ref);
    const answersMap: Record<string, number> = resultItem?.answers || {};

    const entries = Object.entries(answersMap);
    if (!entries.length) return { title: '', percent: 0 };

    const [topRef, topVotes] = entries.reduce((max, curr) =>
      curr[1] > max[1] ? curr : max
    );

    const topAnswer = question.answers?.find(a => a.ref === topRef);
    const totalVotes = entries.reduce((sum, [, value]) => sum + value, 0);
    const percent = totalVotes ? Math.round((topVotes / totalVotes) * 100) : 0;

    return { title: topAnswer?.title || '', percent };
  });

  const quizEnded = result?.quiz.endsDate && dayjs().isAfter(Number(result.quiz.endsDate));

  return (
    <div
      className="h-full flex flex-col overflow-y-auto"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') top / cover` : '' }}
    >
      <PublicDevicePlayable />
      <div className="p-4"><AppBar /></div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="mb-2 flex justify-center items-center gap-3">
          <h2 className="font-bold text-center text-primary text-2xl">{t('pages.results.title')}</h2>
          {!quizEnded ? (
            <Button onClick={handleGetResult} size="small">
              <FiRefreshCcw />
            </Button>
          ) : null}
        </div>
        <div className="relative flex-1 overflow-y-auto bg-cover bg-bottom rounded-xl" style={{ backgroundImage: 'url(/images/teamboard.jpeg)' }}>
          {teamSelectorFormations[quiz.teamSelectorFormation as keyof typeof teamSelectorFormations]?.map((pos, index) => (
            <PlayerSpot
              key={pos.role}
              color={quiz.resultsPage.primaryColor}
              positions={{ x: pos.x, y: pos.y }}
              answer={answers[index] || { title: '', percent: 0 }}
            />
          ))}
        </div>
        {socialShare?.enabled && (socialShare.hashtags || socialShare.url || socialShare.image) ? (
          <Fragment>
            <h3 className="text-center text-sm text-primary font-bold my-2 px-4">{t('pages.results.shareText')}</h3>
            <SocialShare />
          </Fragment>
        ) : null}
        <PoweredBy />
      </div>
    </div>
  )
}

export default TeamBoard;
